import { useMemo } from "react";
import { Box, Checkbox, Divider, TextField, Typography } from "@mui/material";
import { FormikProps } from "formik";
import { AdminOrganizationDetailResDto } from "service/api/estabrookSchemas";
import { makeSxStyles } from "ui";
import FieldRow from "@/shared/components/FieldRow";
import ReplyEmailField from "@/shared/components/ReplyEmailField";
import AddressDerivedFields from "./AddressDerivedFields";
import OrganizationAddressAutocomplete from "./OrganizationAddressAutocomplete";
import PromotionalBannerFields from "./PromotionalBannerFields";
import { EditOrganizationFormValues } from "../types/organization-form";
import { getHomeImageDataUrl } from "../utils/promotional-banner";

interface EditOrganizationFormProps {
  organization: AdminOrganizationDetailResDto;
  organizationForm: FormikProps<EditOrganizationFormValues>;
  homeImageError: string | null;
  onHomeImageErrorDismiss: () => void;
  isActive: boolean;
  onToggleAccessRequest: () => void;
  placePredictions: google.maps.places.AutocompletePrediction[];
  getPlacePredictions: (params: { input: string }) => void;
  getAddressComponents: (
    placeId: string,
  ) => Promise<google.maps.GeocoderAddressComponent[] | null>;
}

const EditOrganizationForm = ({
  organization,
  organizationForm,
  homeImageError,
  onHomeImageErrorDismiss,
  isActive,
  onToggleAccessRequest,
  placePredictions,
  getPlacePredictions,
  getAddressComponents,
}: EditOrganizationFormProps) => {
  const styles = useStyles();
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setValues,
    setFieldValue,
    setFieldTouched,
  } = organizationForm;
  const savedImageUrl = useMemo(
    () => getHomeImageDataUrl(organization.homeImage),
    [organization.homeImage],
  );

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.sectionTitle}>Organization Info</Typography>

      <FieldRow label="Organization Name" required>
        <TextField
          id="name"
          placeholder="Enter Name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.name && !!errors.name}
          helperText={touched.name && errors.name}
          fullWidth
        />
      </FieldRow>
      <FieldRow label="Address" required>
        <OrganizationAddressAutocomplete
          value={values.address ?? ""}
          onAddressChange={(value, parsed) =>
            setValues((prev) => {
              if (parsed) {
                return {
                  ...prev,
                  address: value,
                  city: parsed.city ?? "",
                  state: parsed.state ?? "",
                  zipCode: parsed.zipCode ?? "",
                };
              }

              if (value === organization.address) {
                return {
                  ...prev,
                  address: value,
                  city: organization.city ?? "",
                  state: organization.state,
                  zipCode: organization.zipCode ?? "",
                };
              }

              return {
                ...prev,
                address: value,
                city: "",
                state: "",
                zipCode: "",
              };
            })
          }
          onBlur={handleBlur}
          error={touched.address && !!errors.address}
          helperText={touched.address && errors.address}
          placePredictions={placePredictions}
          getPlacePredictions={getPlacePredictions}
          getAddressComponents={getAddressComponents}
        />
      </FieldRow>
      <AddressDerivedFields values={values} errors={errors} touched={touched} />

      <Divider sx={{ my: "32px" }} />

      <ReplyEmailField
        value={values.replyToEmail ?? ""}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.replyToEmail && !!errors.replyToEmail}
        helperText={touched.replyToEmail && errors.replyToEmail}
      />

      <Box sx={{ mt: "8px" }}>
        <FieldRow label="Access" align="center">
          <Checkbox
            checked={isActive}
            onChange={onToggleAccessRequest}
            data-testid="edit-organization-access-toggle"
          />
        </FieldRow>
      </Box>

      <PromotionalBannerFields
        values={values}
        errors={errors}
        touched={touched}
        handleChange={handleChange}
        handleBlur={handleBlur}
        setFieldValue={setFieldValue}
        setFieldTouched={setFieldTouched}
        savedImageUrl={savedImageUrl}
        uploadError={homeImageError}
        onUploadErrorDismiss={onHomeImageErrorDismiss}
      />
    </Box>
  );
};

const useStyles = makeSxStyles(() => ({
  container: {
    mt: "24px",
    pb: "40px",
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: "24px",
    lineHeight: "32px",
    mb: "32px",
  },
}));

export default EditOrganizationForm;
